import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Users, Plus, Music2, Mic2, ArrowLeft, Loader2, FileText } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Header } from "@/components/Header";
import { OtpGate } from "@/components/OtpGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputStyle = { background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" };

function fmtErr(d) { return typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg || "").join(" ") : "Something went wrong."; }

export default function Workshop() {
  const { t, lang } = useLang();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [gate, setGate] = useState(false);
  const [token, setToken] = useState(() => sessionStorage.getItem("sk_ws_token"));
  const [form, setForm] = useState({ title: "", lyrics: "", author_name: "", tags: "" });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/workshop/projects`).then((r) => setProjects(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!showForm) return;
    const h = (e) => { if (e.key === "Escape") setShowForm(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [showForm]);

  const startPost = () => { if (token) setShowForm(true); else setGate(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error(t("noncommercial_note")); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/workshop/projects`, { token, ...form, consent: true });
      toast.success("Posted");
      setShowForm(false); setForm({ title: "", lyrics: "", author_name: "", tags: "" }); setConsent(false);
      await load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen pb-16" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 sk-fade-up">
          <div>
            <span className="grid place-items-center w-14 h-14 rounded-2xl sk-glow mb-3" style={{ background: "var(--player-bg)", color: "var(--player-accent)" }}><Users size={26} /></span>
            <h1 className={`${lang === "si" ? "font-si" : "font-en"} text-3xl sm:text-4xl font-bold`} style={{ color: "var(--text)" }}>{t("ws_title")}</h1>
            <p className="font-ui text-base mt-2 max-w-2xl leading-relaxed" style={{ color: "var(--text-2)" }}>{t("ws_sub")}</p>
          </div>
          <button onClick={startPost} data-testid="workshop-post-button" className="shrink-0 inline-flex items-center gap-2 h-13 h-[52px] px-6 rounded-full font-ui font-bold" style={{ background: "var(--accent)", color: "#fff" }}>
            <Plus size={20} /> {t("ws_post")}
          </button>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="animate-spin inline" style={{ color: "var(--text-muted)" }} /></div>
        ) : projects.length === 0 ? (
          <div className="py-16 text-center font-ui text-lg" style={{ color: "var(--text-muted)" }}>{t("ws_empty")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {projects.map((p) => (
              <Link key={p.id} to={`/rooms/sindu-kamare/workshop/${p.id}`} data-testid={`workshop-project-${p.id}`} className="rounded-3xl p-5 transition-transform hover:scale-[1.02] sk-fade-up" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
                <div className="flex items-center gap-2 mb-2" style={{ color: "var(--accent)" }}><FileText size={18} /><span className="font-ui text-xs font-bold uppercase tracking-wide">{t("ws_field_lyrics")}</span></div>
                <h3 className={`${lang === "si" ? "font-si" : "font-en"} text-xl font-bold`} style={{ color: "var(--text)" }}>{p.title}</h3>
                <p className="font-ui text-sm mt-1" style={{ color: "var(--text-muted)" }}>{t("ws_by")} {p.author_name}</p>
                <p className="font-si text-sm mt-3 line-clamp-3 whitespace-pre-line" style={{ color: "var(--text-2)" }}>{p.lyrics}</p>
                <div className="flex gap-4 mt-4 font-ui text-sm font-semibold" style={{ color: "var(--text-2)" }}>
                  <span className="flex items-center gap-1"><Music2 size={16} /> {p.melody_count}</span>
                  <span className="flex items-center gap-1"><Mic2 size={16} /> {p.vocal_count}</span>
                </div>
              </Link>
            ))}
          </div>
        )}

        <Link to="/rooms/sindu-kamare" className="flex items-center justify-center gap-2 font-ui text-sm font-semibold mt-10" style={{ color: "var(--accent)" }}><ArrowLeft size={16} /> {t("back_room")}</Link>
      </div>

      {gate && <OtpGate onVerified={(tok) => { sessionStorage.setItem("sk_ws_token", tok); setToken(tok); setGate(false); setShowForm(true); }} onClose={() => setGate(false)} />}

      {showForm && (
        <div className="fixed inset-0 z-50 grid place-items-center px-4 py-8 overflow-y-auto" style={{ background: "rgba(0,0,0,0.5)" }} onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <form onSubmit={submit} className="w-full max-w-lg rounded-3xl p-6 space-y-4 sk-scale-in" style={{ background: "var(--card)", border: "2px solid var(--border)" }} data-testid="workshop-new-form">
            <h3 className="font-ui text-xl font-bold" style={{ color: "var(--text)" }}>{t("ws_new_title")}</h3>
            <label className="block">
              <span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("ws_field_title")} *</span>
              <input data-testid="workshop-field-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={inputStyle} />
            </label>
            <label className="block">
              <span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("ws_field_lyrics")} *</span>
              <textarea data-testid="workshop-field-lyrics" required rows={6} value={form.lyrics} onChange={(e) => setForm({ ...form, lyrics: e.target.value })} className="mt-1 w-full px-4 py-3 rounded-xl font-si outline-none resize-y" style={inputStyle} />
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("ws_field_author")} *</span>
                <input data-testid="workshop-field-author" required value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={inputStyle} />
              </label>
              <label className="block">
                <span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("ws_field_tags")}</span>
                <input data-testid="workshop-field-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={inputStyle} />
              </label>
            </div>
            <label className="flex items-start gap-3 cursor-pointer rounded-2xl p-4" style={{ background: "var(--brass-light)" }}>
              <input data-testid="workshop-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="w-6 h-6 mt-0.5 accent-amber-600 shrink-0" />
              <span className="font-ui text-sm leading-relaxed" style={{ color: "var(--text)" }}>{t("consent_label")}</span>
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 h-12 rounded-xl font-ui font-bold" style={{ background: "var(--bg)", color: "var(--text)", border: "1px solid var(--border)" }}>{t("cancel")}</button>
              <button data-testid="workshop-new-submit" disabled={busy} type="submit" className="flex-1 h-12 rounded-xl font-ui font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>{busy ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />} {t("post")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
